import * as rxjs from "rxjs";
import * as rxjsOperators from "rxjs/operators";
import * as TGetState from "../../TGetState";
import * as TSetState from "../../TSetState";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";
import * as JsonResponse1 from "../../../../../domain/models/JsonResponse1";
import * as t from "io-ts";
import { TActionsDefinitionsList } from "../TAction";
import { TActionObservable } from "../../applyActions";
import * as fetchWrapper from "../../../wrappers/fetch";
import { option } from "fp-ts";

declare var grecaptcha: {
    ready: () => void,
    execute: (publicKey: string, p: {action: string}) => Promise<string>,
};

const RECAPTCHA_ACTION = "ListingInfoRequest";

const setField = <K extends "firstName" | "lastName" | "contactEmail" | "contactPhoneNumber">(
    type: "LISTING_INFO_REQUEST_FIRST_NAME_CHANGED" | "LISTING_INFO_REQUEST_LAST_NAME_CHANGED" | "LISTING_INFO_REQUEST_EMAIL_CHANGED" | "LISTING_INFO_REQUEST_PHONE_CHANGED",
    field: K,
) => ({
    type,
    run: (
        obs$: TActionObservable<typeof type, string>,
        _getState: TGetState.TGetState,
        setState: TSetState.TSetState,
    ): void => {
        obs$.subscribe(({payload}) => {
            setState(({...s}) => {
                s.activeData.listingInfoRequest[field] = payload;
                return s;
            });
        });
    },
});

export const actions: TActionsDefinitionsList = [
    setField("LISTING_INFO_REQUEST_FIRST_NAME_CHANGED", "firstName"),
    setField("LISTING_INFO_REQUEST_LAST_NAME_CHANGED", "lastName"),
    setField("LISTING_INFO_REQUEST_EMAIL_CHANGED", "contactEmail"),
    setField("LISTING_INFO_REQUEST_PHONE_CHANGED", "contactPhoneNumber"),
    {
        type: "LISTING_INFO_REQUEST_SUBMITTED" as const,
        run: (
            obs$: TActionObservable<"LISTING_INFO_REQUEST_SUBMITTED", string>,
            getState: TGetState.TGetState,
            setState: TSetState.TSetState,
        ): void => {
            obs$.pipe(
                rxjsOperators.tap(({payload: listingId}) => setState(({...s}) => {
                    s.activeData.listingInfoRequest.submitting = true;
                    s.activeData.listingInfoRequest.failed = false;
                    s.activeData.listingInfoRequest.forListingId = listingId;
                    return s;
                })),
                rxjsOperators.mergeMap(({payload: listingId}) =>
                    rxjs.from(grecaptcha.execute(env.REACT_APP_RECAPTCHA_PUBLIC_KEY, { action: RECAPTCHA_ACTION })).pipe(
                        rxjsOperators.mergeMap((recaptchaToken) => {
                            const form = getState().activeData.listingInfoRequest;

                            return rxjs.from(fetchWrapper.json<JsonResponse1.T<{}, {}>>({
                                requestParams: {
                                    url: `${env.REACT_APP_API_URL}/v1/public/listings/${listingId}/info-requests`,
                                    method: "POST",
                                    body: option.some(JSON.stringify({
                                        first_name: form.firstName,
                                        last_name: form.lastName,
                                        contact_email: form.contactEmail,
                                        contact_phone_number: form.contactPhoneNumber,
                                        recaptcha_token: recaptchaToken,
                                    })),
                                },
                                expectedTypeCodec: JsonResponse1.createCodec(t.type({}), t.type({})),
                                defaultResponse: JsonResponse1.create({}),
                            })());
                        }),
                        rxjsOperators.map((response) => setState(({...s}) => {
                            s.activeData.listingInfoRequest.submitting = false;

                            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                                s.activeData.listingInfoRequest.submitted = true;
                            } else {
                                s.activeData.listingInfoRequest.failed = true;
                            }

                            return s;
                        })),
                        // A buyer who has typed their details must not be left on a spinner.
                        rxjsOperators.catchError(() => rxjs.of(setState(({...s}) => {
                            s.activeData.listingInfoRequest.submitting = false;
                            s.activeData.listingInfoRequest.failed = true;
                            return s;
                        }))),
                    )
                ),
            ).subscribe();
        },
    },
];
